import { Target, X } from 'lucide-react'

import { cn } from 'lib/utils'

interface ScopeChipProps {
	/** Plural row label, e.g. "Tasks". */
	grainLabel: string
	/** The asset's name; null while it is being looked up. */
	assetName: string | null
	/** The asset's kind, used when its name cannot be shown. */
	assetType: string
	onClear: () => void
	className?: string
}

/**
 * "Tasks of: Hollister Safeway Remodel ×" (MYD-598).
 *
 * Says that the grid is narrowed to one asset, which is otherwise invisible: a
 * scoped report and an unscoped one look identical apart from the row count.
 * Removing it widens the report back to everything the viewer can see and marks
 * it changed, so saving afterwards stores it unscoped.
 */
export function ScopeChip({ grainLabel, assetName, assetType, onClear, className }: ScopeChipProps) {
	const label = assetName ?? `this ${assetType}`

	return (
		<div
			className={cn(
				'inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 py-1 pl-3 pr-1 text-sm',
				className,
			)}
			data-testid="explorer-scope-chip"
		>
			<Target className="h-3.5 w-3.5 shrink-0 text-primary" />
			<span className="text-gray-600">{grainLabel} of:</span>
			<span className="truncate font-medium text-gray-900">{label}</span>
			<button
				type="button"
				onClick={onClear}
				aria-label={`Show all ${grainLabel.toLowerCase()}, not just ${label}`}
				title="Remove this scope"
				className="ml-0.5 rounded-full p-1 text-gray-500 transition-colors hover:bg-primary/10 hover:text-gray-900"
			>
				<X className="h-3.5 w-3.5" />
			</button>
		</div>
	)
}

export default ScopeChip
